import { useCallback } from 'react'

import type { one_to_one } from '@fireworkhq/proto-contract'
import { useMessagePreviewContext } from '@liveHelper/components/MessagePreviewProvider'
import { getLiveHelperUIContext } from '@liveHelper/models/fsm/machine'
import {
  actionsSelector,
  conversationSelector,
  hasSalesAgentMessagesInConversation,
  hasSentMessageSelector,
  paramsSelector,
} from '@liveHelper/models/store'
import { useStoreApi } from '@liveHelper/models/store/libs/store'
import { customLiveHelperEvent } from '@liveHelper/tracking/customLiveHelperEvent'
import { EventType } from '@liveHelper/tracking/eventType'
import { playNotification } from '@oneToOne/shared/utils/audio'
import { useTypingStatusTracker } from '@oneToOne/shared/utils/useTypingStatusTracker'
import type { MessageChannel } from '@oneToOne/shared/ws/message'

/** Handles live helper message channel message. */
export function useHandleServerMessages() {
  const storeApi = useStoreApi()
  const { addChatMessages, replaceMessage, updateOtherPartyTypingStatus } =
    actionsSelector(storeApi.getState())

  const { setIsTyping } = useTypingStatusTracker({
    callback: updateOtherPartyTypingStatus,
  })

  const { showMessagePreview } = useMessagePreviewContext()

  return useCallback(
    (channel: MessageChannel | undefined) => {
      const incomingMessageRef = channel?.on('incoming_message', (payload) => {
        const { widgetState } = getLiveHelperUIContext()

        const { agentMessages, hasMessageFromAgent, hasTextOrMediaMessage } =
          agentMessagesPayload(payload.messages ?? [])

        if (hasMessageFromAgent) {
          showMessagePreview()
          // To ensure that the typing status is accurately maintained for new agent messages,
          // we clear the typing timeout when a new agent message is received.
          // This prevents the typing status from being incorrectly set to false when the timeout is reached, which is undesirable behavior.
          setIsTyping(false)

          const conversation = conversationSelector(storeApi.getState())
          if (
            conversation &&
            hasSentMessageSelector(storeApi.getState()) &&
            !hasSalesAgentMessagesInConversation(conversation)(
              storeApi.getState(),
            )
          ) {
            customLiveHelperEvent({
              type: EventType.FW_LIVE_HELPER_AGENT_ANSWER_CHAT,
              data: {
                widget_id: paramsSelector(storeApi.getState()).widget_id,
                agent_id: conversation.agent?.id,
                agent_name: conversation.agent?.name,
              },
            })
          }
        }

        addChatMessages(agentMessages, 'visitor')

        const isWidgetMinimized = widgetState === 'minimized'

        if (
          hasTextOrMediaMessage &&
          (isWidgetMinimized || !document.hasFocus())
        ) {
          playNotification()
        }
      })

      const incomingTypingEventRef = channel?.on('is_typing', () => {
        setIsTyping(true)
      })

      const linkPreviewRef = channel?.on('link_preview', (payload) => {
        payload.messages?.forEach((message) => {
          replaceMessage(message, 'visitor')
        })
      })

      return () => {
        channel?.off('incoming_message', incomingMessageRef)
        channel?.off('is_typing', incomingTypingEventRef)
        channel?.off('link_preview', linkPreviewRef)
      }
    },
    [
      addChatMessages,
      setIsTyping,
      showMessagePreview,
      replaceMessage,
      storeApi,
    ],
  )
}

const agentMessagesPayload = (messages: one_to_one.shared.Message[]) => {
  return messages?.reduce(
    (acc, message) => {
      const author =
        message.text_message?.author || message.media_message?.author

      if (author !== 'AUTHOR_VISITOR') {
        acc.hasMessageFromAgent =
          acc.hasMessageFromAgent || author === 'AUTHOR_AGENT'
        acc.agentMessages.push(message)
        acc.hasTextOrMediaMessage =
          acc.hasTextOrMediaMessage ||
          Boolean(message.text_message) ||
          Boolean(message.media_message)
      }

      return acc
    },
    {
      agentMessages: [] as one_to_one.shared.Message[],
      hasMessageFromAgent: false,
      hasTextOrMediaMessage: false,
    },
  )
}
